import React, { useState, useEffect } from 'react';
import { FiBook, FiAward, FiUser, FiBarChart2, FiEdit, FiSave, FiX } from 'react-icons/fi';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { getLearnerMenu } from '../../components/layout/menuConfigs';
import EducationalParticles from '../../components/common/EducationalParticles';
import Button from '../../components/common/Button';
import Input from '../../components/common/Input';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { showToast } from '../../components/common/Toaster';
import { useTheme } from '../../contexts/ThemeContext';
import { 
  DashboardContainer, 
  DashboardHeader, 
  StatsGrid, 
  StatCard, 
  StatIcon, 
  StatInfo, 
  SectionTitle,
  ContentCard,
  ContentCardHeader,
  ContentCardBody,
} from '../dashboards/Dashboard.styles';

const ProfilePage = () => {
  const { theme } = useTheme();
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [profile, setProfile] = useState({
    username: '',
    email: '',
    first_name: '',
    last_name: '',
    grade: '',
    created_at: '',
  });

  const menuItems = getLearnerMenu();

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      // Mock profile data - replace with actual API call
      setProfile({
        username: 'student_user',
        email: 'student@example.com',
        first_name: 'John',
        last_name: 'Doe',
        grade: 'Grade 5',
        created_at: '2024-01-01T00:00:00Z',
      });
    } catch (error) {
      showToast('error', 'Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSubmitting(true);
      // Mock save - replace with actual API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      showToast('success', 'Profile updated successfully!');
      setEditing(false);
    } catch (error) {
      showToast('error', 'Failed to update profile');
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  if (loading) {
    return (
      <DashboardLayout menuItems={menuItems}>
        <DashboardContainer>
          <LoadingSpinner size="large" text="Loading profile..." />
        </DashboardContainer>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout menuItems={menuItems}>
      <EducationalParticles />
      <DashboardContainer>
        <DashboardHeader>
          <div>
            <h1>My Profile 👤</h1>
            <p>Manage your account information and preferences</p>
          </div>
          <div style={{ display: 'flex', gap: '12px' }}>
            {editing ? (
              <>
                <Button 
                  variant="ghost" 
                  onClick={() => setEditing(false)}
                >
                  <FiX /> Cancel
                </Button>
                <Button 
                  onClick={handleSave} 
                  loading={submitting}
                >
                  <FiSave /> Save Changes
                </Button>
              </>
            ) : (
              <Button 
                onClick={() => setEditing(true)}
              >
                <FiEdit /> Edit Profile
              </Button>
            )}
          </div>
        </DashboardHeader>

        {/* Profile Stats */}
        <StatsGrid>
          <StatCard>
            <StatIcon color="#2c5f34">
              <FiUser />
            </StatIcon>
            <StatInfo>
              <h3>Student</h3>
              <p>Account Type</p>
            </StatInfo>
          </StatCard>

          <StatCard>
            <StatIcon color="#10B981">
              <FiBook />
            </StatIcon>
            <StatInfo>
              <h3>{profile.grade}</h3>
              <p>Grade Level</p>
            </StatInfo>
          </StatCard>

          <StatCard>
            <StatIcon color="#F59E0B">
              <FiAward />
            </StatIcon>
            <StatInfo>
              <h3>Active</h3>
              <p>Account Status</p>
            </StatInfo>
          </StatCard>

          <StatCard>
            <StatIcon color="#8B5CF6">
              <FiBarChart2 />
            </StatIcon>
            <StatInfo>
              <h3>{formatDate(profile.created_at)}</h3>
              <p>Member Since</p>
            </StatInfo>
          </StatCard>
        </StatsGrid>

        {/* Profile Information */}
        <SectionTitle>Profile Information</SectionTitle>
        <ContentCard>
          <ContentCardHeader>
            <h3>Personal Details</h3>
          </ContentCardHeader>
          <ContentCardBody>
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', 
              gap: '20px' 
            }}>
              <div>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '8px', 
                  fontWeight: '500',
                  color: theme.colors.textPrimary 
                }}>
                  Username
                </label>
                {editing ? (
                  <Input
                    value={profile.username}
                    onChange={(e) => setProfile({ ...profile, username: e.target.value })}
                    placeholder="Enter username"
                  />
                ) : (
                  <div style={{ 
                    padding: '12px 16px', 
                    background: theme.colors.surface,
                    border: `1px solid ${theme.colors.border}`,
                    borderRadius: '8px',
                    color: theme.colors.textPrimary
                  }}>
                    {profile.username}
                  </div>
                )}
              </div>

              <div>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '8px', 
                  fontWeight: '500',
                  color: theme.colors.textPrimary 
                }}>
                  Email
                </label>
                {editing ? (
                  <Input
                    type="email"
                    value={profile.email}
                    onChange={(e) => setProfile({ ...profile, email: e.target.value })}
                    placeholder="Enter email"
                  />
                ) : (
                  <div style={{ 
                    padding: '12px 16px', 
                    background: theme.colors.surface,
                    border: `1px solid ${theme.colors.border}`,
                    borderRadius: '8px',
                    color: theme.colors.textPrimary
                  }}>
                    {profile.email}
                  </div>
                )}
              </div>

              <div>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '8px', 
                  fontWeight: '500',
                  color: theme.colors.textPrimary 
                }}>
                  First Name
                </label>
                {editing ? (
                  <Input
                    value={profile.first_name}
                    onChange={(e) => setProfile({ ...profile, first_name: e.target.value })}
                    placeholder="Enter first name"
                  />
                ) : (
                  <div style={{ 
                    padding: '12px 16px', 
                    background: theme.colors.surface,
                    border: `1px solid ${theme.colors.border}`,
                    borderRadius: '8px',
                    color: theme.colors.textPrimary
                  }}>
                    {profile.first_name}
                  </div>
                )}
              </div>

              <div>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '8px', 
                  fontWeight: '500',
                  color: theme.colors.textPrimary 
                }}>
                  Last Name
                </label>
                {editing ? (
                  <Input
                    value={profile.last_name}
                    onChange={(e) => setProfile({ ...profile, last_name: e.target.value })}
                    placeholder="Enter last name"
                  />
                ) : (
                  <div style={{ 
                    padding: '12px 16px', 
                    background: theme.colors.surface,
                    border: `1px solid ${theme.colors.border}`,
                    borderRadius: '8px',
                    color: theme.colors.textPrimary
                  }}>
                    {profile.last_name}
                  </div>
                )}
              </div>

              <div>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '8px', 
                  fontWeight: '500',
                  color: theme.colors.textPrimary 
                }}>
                  Grade Level
                </label>
                <div style={{ 
                  padding: '12px 16px', 
                  background: theme.colors.surface,
                  border: `1px solid ${theme.colors.border}`,
                  borderRadius: '8px',
                  color: theme.colors.textPrimary
                }}>
                  {profile.grade}
                </div>
              </div>

              <div>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '8px', 
                  fontWeight: '500',
                  color: theme.colors.textPrimary 
                }}>
                  Member Since
                </label>
                <div style={{ 
                  padding: '12px 16px', 
                  background: theme.colors.surface,
                  border: `1px solid ${theme.colors.border}`,
                  borderRadius: '8px',
                  color: theme.colors.textPrimary
                }}>
                  {formatDate(profile.created_at)}
                </div>
              </div>
            </div>
          </ContentCardBody>
        </ContentCard>

        {/* Account Settings */}
        <SectionTitle style={{ marginTop: '30px' }}>Account Settings</SectionTitle>
        <ContentCard>
          <ContentCardHeader>
            <h3>Security & Privacy</h3>
          </ContentCardHeader>
          <ContentCardBody>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              padding: '16px 0',
              borderBottom: `1px solid ${theme.colors.border}`
            }}>
              <div>
                <h4 style={{ margin: '0 0 4px 0', color: theme.colors.textPrimary }}>
                  Change Password
                </h4>
                <p style={{ margin: 0, color: theme.colors.textSecondary }}>
                  Update your account password
                </p>
              </div>
              <Button variant="ghost">
                Change
              </Button>
            </div>
            
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              padding: '16px 0',
              borderBottom: `1px solid ${theme.colors.border}`
            }}>
              <div>
                <h4 style={{ margin: '0 0 4px 0', color: theme.colors.textPrimary }}>
                  Privacy Settings
                </h4>
                <p style={{ margin: 0, color: theme.colors.textSecondary }}>
                  Manage your privacy preferences
                </p>
              </div>
              <Button variant="ghost">
                Manage
              </Button>
            </div>
            
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              padding: '16px 0'
            }}>
              <div>
                <h4 style={{ margin: '0 0 4px 0', color: theme.colors.textPrimary }}>
                  Account Deletion
                </h4>
                <p style={{ margin: 0, color: theme.colors.textSecondary }}>
                  Permanently delete your account
                </p>
              </div>
              <Button variant="ghost" style={{ color: '#EF4444' }}>
                Delete Account
              </Button>
            </div>
          </ContentCardBody>
        </ContentCard>
      </DashboardContainer>
    </DashboardLayout>
  );
};

export default ProfilePage;
